import type {
    ICreateSnapshotMetadata,
    ISnapshotResponse,
    IUpdateSnapshot,
    TSnapshotBaseVariant,
} from '@escloud/types';

import type { ApiClient } from './api-client';

/** Payload for creating a snapshot: wire metadata + the captured PNG data URLs. */
export interface CreateSnapshotInput {
    metadata: ICreateSnapshotMetadata;
    /** Base image WITH overlays (PNG data URL). Present for user snapshots; omitted for template annotations. */
    imageDataUrl?: string;
    /** Base image WITHOUT overlays (PNG data URL). User captures only. */
    imageNoOverlayDataUrl?: string;
}

/**
 * API service for snapshot persistence (`/cases/:caseId/snapshots`). Endpoint
 * plumbing only — no business logic. Distinct from the visualization-layer
 * `ViewportSnapshotService` which owns the in-session snapshot store.
 */
export class SnapshotsService {
    constructor(private api: ApiClient) {}

    /**
     * Create a snapshot via multipart form-data: a JSON `metadata` field plus,
     * for user snapshots, the PNG image part(s). Template annotations send
     * metadata only — the base image is regenerated client-side.
     */
    async create(caseId: string, input: CreateSnapshotInput): Promise<ISnapshotResponse> {
        const form = new FormData();
        form.append('metadata', JSON.stringify(input.metadata));
        if (input.imageDataUrl) {
            form.append('image', await dataUrlToBlob(input.imageDataUrl), 'overlay.png');
        }
        if (input.imageNoOverlayDataUrl) {
            form.append('imageNoOverlay', await dataUrlToBlob(input.imageNoOverlayDataUrl), 'no-overlay.png');
        }
        return this.api.postMultipart<ISnapshotResponse>(`/cases/${caseId}/snapshots`, form);
    }

    /** List non-deleted snapshot metadata for a case (no image bytes). */
    list(caseId: string, signal?: AbortSignal): Promise<ISnapshotResponse[]> {
        return this.api.get<ISnapshotResponse[]>(`/cases/${caseId}/snapshots`, { signal });
    }

    /** Fetch a snapshot image variant and return it as a PNG data URL. */
    async getImage(
        caseId: string,
        id: string,
        variant: TSnapshotBaseVariant,
        signal?: AbortSignal
    ): Promise<string> {
        const buffer = await this.api.getBinary(`/cases/${caseId}/snapshots/${id}/image?variant=${variant}`, {
            signal,
        });
        return blobToDataUrl(new Blob([buffer], { type: 'image/png' }));
    }

    /** Patch re-editable metadata (description, includedInReport, annotations, baseVariant). */
    update(caseId: string, id: string, patch: IUpdateSnapshot): Promise<ISnapshotResponse> {
        return this.api.patch<ISnapshotResponse>(`/cases/${caseId}/snapshots/${id}`, patch);
    }

    /** Soft-delete a snapshot. */
    remove(caseId: string, id: string): Promise<void> {
        return this.api.delete<void>(`/cases/${caseId}/snapshots/${id}`);
    }
}

/** Convert a data URL to a Blob (browser decodes the base64 payload). */
function dataUrlToBlob(dataUrl: string): Promise<Blob> {
    return fetch(dataUrl).then((r) => r.blob());
}

/** Convert a Blob to a base64 data URL, matching the in-session snapshot format. */
function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read snapshot image'));
        reader.readAsDataURL(blob);
    });
}
